import React, { useState, useEffect } from 'react'
//components
import { Button } from '../../../../components/ui/buttons/Button'
import { UserJobs } from './UserJobs'
import Label from '../../../../components/ui/labels/Label'
import { Input } from '../../../../components/ui/inputs/Input'
import { InputContainer, InputRow } from '../../../../components/ui/inputs/InputContainers'
import { Toggle } from '../../../../components/ui/inputs/Toggle'
import { APIMessage } from '../../../../components/ui/messages/APIMessage'
import { PasswordValidationErrors } from "../../../../components/ui/messages/PasswordValidationErrors";
//hooks
import { useLoading } from "../../../../contexts/LoadingContext"
import useConfirmDialog from '../../../../hooks/useConfirmDialog'
import { useError } from '../../../../contexts/ErrorContext'
import { useSuccess } from '../../../../contexts/SuccessContext'
//api/utils
// NOTE: jwtDecode removed - user ID should come from response.userId (backend change required)
import { validatePassword } from '../../../../utils/validatePassword'
import { registerAPI } from "../../../../services/authAPI"
import { getUserAPI, updateUserAPI, deleteUserAPI } from '../../../../services/usersAPI'
import { UserGroups } from './UserGroups'

/**
 * 
 * Form for adding a new user or displaying/updating a current user
 * 
 * @param {DropdownOption | null} selectedUser - user selected in parent, looks like: {value: 1, label: "FirstName LastName"}
 * @param {Function} setSelectedUser - function to set selected user in parent when new user created
 * @param {Function} setUserNameOptions - function to set user name options for dropdown in parent, when a new user added
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Admin Dashboard
 */

export const UserForm = ({selectedUser = null, setSelectedUser, setUserNameOptions, isAddNewUser, setIsAddNewUser}) => {
    
    //set state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [position, setPosition] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isActive, setIsActive] = useState(true);

    const [validationErrors, setValidationErrors] = useState({})

    const { showLoading, hideLoading } = useLoading();
    const [ConfirmDialog, requestConfirm] = useConfirmDialog()
    const {showError} =  useError()
    const {timeoutSuccess} = useSuccess()

    //fetch data and set state
    const fetchUserData = async(userID) => {
        showLoading("Getting user data...")
        const response = await getUserAPI(userID);

        if (!response.success) {
            hideLoading()

            showError(`Error getting user: ${response.message}`)

            return
        }
                
        const {First_Name, Last_Name, Email, Phone, Position, IsActive} = response.data

        setFirstName(First_Name)
        setLastName(Last_Name)
        setEmail(Email)
        setPhone(Phone)
        setPosition(Position)
        setIsActive(IsActive)

        hideLoading()
    
    }

    //fetch data when selectedUser changes in the parent
    useEffect(() => {
        if(selectedUser){
            
            fetchUserData(selectedUser.value)

        }

        //if no selectedUser, reset state
        setFirstName("")
        setLastName("")
        setEmail("")
        setPhone("")
        setPosition("")
        setIsActive(true)

    }, [selectedUser]) //eslint-disable-line


    //validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\(?\d{3}\)?[-\s]?\d{3}[-\s]?\d{4}$/;

    const validate = () => {
        const errors = {}

        if(!firstName){
            errors.firstName = "First name is required"
        }

        if(!lastName){
            errors.lastName = "Last name is required"
        }

        if(!email){
            errors.email = "Email is required"
        } else if (!emailRegex.test(email)) {
            errors.email = "Email must be in this format: example@domain.com"
        }

        if(!phone){
            errors.phone = "Phone number is required"
        } else if(!phoneRegex.test(phone)) {
            errors.phone = "Please enter a valid phone number"
        }

        if(!position){
            errors.position = "Position is required"
        }

        //check password only if adding a new user
        if(isAddNewUser){
            const passwordErrors = validatePassword(password);

            if (passwordErrors.length > 0) {
                errors.password = passwordErrors
            }
    
            else if(!confirmPassword){
                errors.confirmPassword = "Please confirm password"
            }
            else if(password !== confirmPassword){
                errors.confirmPassword = "Passwords don't match"
            } 
        }
       
        //if there are errors, set state to errors and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            return false
        }

        setValidationErrors({})

        return true
    }

    //submit new user
    const handleSubmit = async() => {
        showLoading("Adding user...")

        if(!validate()){
            hideLoading()
            showError("Could not add new user - some form fields are missing")
            return
        }

        const response = await registerAPI({
            First_Name: firstName,
            Last_Name: lastName,
            Email: email,
            Phone: phone,
            Position: position,
            Password: password,
            IsActive: isActive,
        })
                        
        if(!response.success){
            hideLoading()

            //show error message
            showError(`Error adding user: ${response.message}`)

            //clear password
            setPassword("")
            setConfirmPassword("")
            
            return
        }

        // Use userId from response (requires backend to return userId field instead of token)
        const userId = response.userId || response.user?.UserID;
        if (!userId) {
            hideLoading();
            showError("Error adding user: No user ID returned from server");
            return;
        }

        const newUser = {
            value: userId,
            label: `${firstName} ${lastName}`
        }

        //remove passwords from state
        setPassword("")
        setConfirmPassword("")
        
        //change selected user to new user
        setSelectedUser(newUser)
        setIsAddNewUser(false)

        //add new user to userOptions
        setUserNameOptions(prevData => {
            const newData = [...prevData, newUser];

            return newData;
        })

        hideLoading()

        timeoutSuccess("User saved")

    }

    //update the user's details
    const handleUpdate = async() => {
        showLoading("Updating user...")

        if(!validate()){
            hideLoading()
            return
        }

        const response = await updateUserAPI(selectedUser.value, {
            First_Name: firstName,
            Last_Name: lastName,
            Email: email,
            Phone: phone,
            Position: position,
            IsActive: isActive,
        })
                        
        if(!response.success){
            hideLoading()

            //show error message
            showError(`Error updating user: ${response.message}`)
            
            return
        }

        //update user's name in the dropdown
        const updatedSelectedUser = {
            value: selectedUser.value, 
            label: `${firstName} ${lastName}`
        }

        setSelectedUser(updatedSelectedUser)
        
        setUserNameOptions(prevData => {
            const newData = prevData.map(nameOption => {
                if(nameOption.value !== selectedUser.value){
                    return nameOption
                }
                return updatedSelectedUser
            })

            return newData;
        })

        hideLoading()

        timeoutSuccess("User saved")
    }

    //delete user
    const handleDeleteUser = async() => {
        const isConfirmed = await requestConfirm('Are you sure you want to delete this user?', 'Delete User', 'Cancel');
        if(!isConfirmed) return

        showLoading("Deleting user...")

        const response = await deleteUserAPI(selectedUser.value)

        if(!response.success){
            hideLoading()

            //show error message
            showError(`Error deleting user: ${response.message}`)
    
            return
        }

        hideLoading()

        setSelectedUser(null)

        //remove user from userNameOptions
        setUserNameOptions(prevData => {
            const newData = prevData.filter(user => user.value !== selectedUser.value);

            return newData;
        })

        timeoutSuccess("User deleted successfully")

    }

    return (

        <div className='flex flex-col'>
            
            {!isAddNewUser ? (
                <hr className='mb-12 mt-2'/>
            ) : null}

            <InputRow>
                {/* first name */}
                <InputContainer>
                    <Label htmlFor='firstName' className='mb-2 font-semibold'>First name</Label>
                    <Input 
                        name="firstName" 
                        value={firstName} 
                        onChange={(e)=>setFirstName(e.target.value)}
                        error={validationErrors.firstName}
                    />
                    <APIMessage message={validationErrors.firstName} isError={true} className="my-1 italic text-sm"/>
                </InputContainer>
            
                {/* last name */}
                <InputContainer>
                    <Label htmlFor='lastName' className='mb-2 font-semibold'>Last name</Label>
                    <Input 
                        name="lastName" 
                        value={lastName} 
                        onChange={(e)=>setLastName(e.target.value)} 
                        error={validationErrors.lastName}
                    />
                    <APIMessage message={validationErrors.lastName} isError={true} className="my-1 italic text-sm"/>
                </InputContainer>

            </InputRow>
                
            <InputRow>
                {/* email */}
                <InputContainer>
                    <Label htmlFor='email' className='mb-2 font-semibold'>Email</Label>
                    <Input 
                        name="email" 
                        value={email} 
                        onChange={(e)=>setEmail(e.target.value)}
                        error={validationErrors.email}
                    />
                    <APIMessage message={validationErrors.email} isError={true} className="my-1 italic text-sm"/>
                </InputContainer>

                {/* phone */}
                <InputContainer>
                    <Label htmlFor='phone' className='mb-2 font-semibold'>Phone</Label>
                    <Input 
                        name="phone" 
                        value={phone}
                        onChange={(e)=>setPhone(e.target.value)} 
                        error={validationErrors.phone}
                    />
                    <APIMessage message={validationErrors.phone} isError={true} className="my-1 italic text-sm"/>
                </InputContainer>
            </InputRow>

            {/* position */}
            <InputContainer>
                <Label htmlFor='position' className='mb-2 font-semibold'>Job Title</Label>
                <Input
                    name="position"
                    value={position}
                    onChange={(e)=>setPosition(e.target.value)}
                    error={validationErrors.position}
                />
                <APIMessage message={validationErrors.position} isError={true} className="my-1 italic text-sm"/>
            </InputContainer>

            {/* active toggle */}
            <InputContainer>
                <Toggle
                    checked={isActive}
                    id="isActive"
                    labelText="Active"
                    setParentState={setIsActive}
                />
            </InputContainer>

            {/* only show password inputs if adding a new user */}
            {isAddNewUser
            
                ? (
                    <InputRow>
                
                        {/* password */}
                        <InputContainer>
                            <Label htmlFor='pass' className='mb-2 font-semibold'>Password</Label>
                            <Input 
                                type='text' 
                                name="pass" 
                                value={password} 
                                onChange={(e)=>setPassword(e.target.value)}
                                error={validationErrors.password}
                                className="obscured-password"
                            />
                            <PasswordValidationErrors validationErrors={validationErrors} className="my-1 italic text-sm"/>
                        </InputContainer>

                        {/* confirm password */}
                        <InputContainer>
                            <Label htmlFor='confirmPassword' className='mb-2 font-semibold'>Confirm password</Label>
                            <Input 
                                type='text' 
                                name="confirmPassword" 
                                value={confirmPassword} 
                                onChange={(e)=>setConfirmPassword(e.target.value)}
                                error={validationErrors.confirmPassword}
                                className="obscured-password"
                            />
                            <APIMessage message={validationErrors.confirmPassword} isError={true} className="my-1 italic text-sm"/>
                        </InputContainer>

                    </InputRow>
                )
            : null
            }

            {/* show add user button only if adding new user */}
            {isAddNewUser
                ? (
                <div className='my-4'>
                    <Button variant="success" onClick={() => handleSubmit()} className='mr-2'>Add User</Button>
                    <Button variant="cancel" onClick={() => setIsAddNewUser(false)}>Cancel</Button>
                </div>
                ) 
                : null
            }

            {/* show delete button only if user already exists */}
            {!isAddNewUser 
                ? (
                    <>
                        <div className='my-4'>
                            <Button variant="success" onClick={() => handleUpdate()}>Update User Details</Button>
                        </div>

                        <div className='my-4'>
                            
                            <hr className='mb-10 mt-2'/>

                            {/* show users jobs */}
                            <UserJobs selectedUser={selectedUser} />

                            <hr className='mb-10 mt-2'/>

                            {/* show users groups */}
                            <UserGroups selectedUser={selectedUser} />
                            
                            <hr className='mb-10 mt-2'/>

                            {/* delete user button and error message */}
                            <Button variant="danger" onClick={() => handleDeleteUser()}><i className="fa-solid fa-trash-can mr-2"></i>Delete User</Button>

                        </div>
                    </>

                ) 
                : null
            }

            {/* dialog box opens when "Delete User" button is clicked */}
            <ConfirmDialog />
            
        </div>

    )
}